import React, { useState, useEffect } from 'react';
import { Card, Row, Col, DatePicker, Spin, Empty, Tag, Avatar, Tooltip, Space, Select, Button } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, UserOutlined, ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { apiRoot } from '../../services/apiInstances';
import './OpenSlotsPanel.css';

const { RangePicker } = DatePicker;

interface OpenSlot {
  technician_id: number;
  technician_name: string;
  date: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  workload_percentage: number;
  is_available: boolean;
}

interface TechnicianSummary {
  id: number;
  name: string;
  role: string;
  total_slots: number;
  available_slots: number;
  avg_workload: number;
  earliest_slot: string | null;
}

interface OpenSlotsPanelProps {
  defaultDateRange?: [Dayjs, Dayjs];
  onSlotClick?: (slot: OpenSlot) => void;
  filterByTechId?: number;
  minDuration?: number; // Filter slots by minimum duration
}

export const OpenSlotsPanel: React.FC<OpenSlotsPanelProps> = ({
  defaultDateRange,
  onSlotClick,
  filterByTechId,
  minDuration = 0
}) => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(
    defaultDateRange || [dayjs(), dayjs().add(7, 'days')]
  );
  const [openSlots, setOpenSlots] = useState<OpenSlot[]>([]);
  const [technicianSummaries, setTechnicianSummaries] = useState<TechnicianSummary[]>([]);
  const [selectedTechId, setSelectedTechId] = useState<number | null>(filterByTechId || null);
  const [durationFilter, setDurationFilter] = useState<number>(minDuration);

  useEffect(() => {
    fetchOpenSlots();
  }, [dateRange, selectedTechId, durationFilter]);

  useEffect(() => {
    if (filterByTechId !== undefined) {
      setSelectedTechId(filterByTechId);
    }
  }, [filterByTechId]);

  const fetchOpenSlots = async () => {
    setLoading(true);
    try {
      const [startDate, endDate] = dateRange;

      const response = await apiRoot.get('/appointments/open-slots', {
        params: {
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD'),
          technician_id: selectedTechId || undefined,
          min_duration: durationFilter || undefined
        }
      });

      const slots: OpenSlot[] = response.data.slots || [];
      setOpenSlots(slots);

      // Calculate technician summaries
      const summaries = calculateTechnicianSummaries(slots);
      setTechnicianSummaries(summaries);
    } catch (error) {
      console.error('Error fetching open slots:', error);
      setOpenSlots([]);
      setTechnicianSummaries([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateTechnicianSummaries = (slots: OpenSlot[]): TechnicianSummary[] => {
    const techMap = new Map<number, TechnicianSummary>();

    slots.forEach(slot => {
      if (!techMap.has(slot.technician_id)) {
        techMap.set(slot.technician_id, {
          id: slot.technician_id,
          name: slot.technician_name,
          role: '', // Would need to fetch from API
          total_slots: 0,
          available_slots: 0,
          avg_workload: 0,
          earliest_slot: null
        });
      }

      const summary = techMap.get(slot.technician_id)!;
      summary.total_slots++;

      if (slot.is_available) {
        summary.available_slots++;

        // Track earliest available slot
        if (!summary.earliest_slot || slot.start_time < summary.earliest_slot) {
          summary.earliest_slot = slot.start_time;
        }
      }
    });

    // Calculate average workload
    techMap.forEach(summary => {
      const techSlots = slots.filter(s => s.technician_id === summary.id);
      const totalWorkload = techSlots.reduce((sum, s) => sum + s.workload_percentage, 0);
      summary.avg_workload = techSlots.length > 0 ? totalWorkload / techSlots.length : 0;
    });

    return Array.from(techMap.values()).sort((a, b) => b.available_slots - a.available_slots);
  };

  const getWorkloadColor = (workload: number): string => {
    if (workload < 50) return '#52c41a'; // Green - low workload
    if (workload < 75) return '#faad14'; // Yellow - medium workload
    if (workload < 90) return '#fa8c16'; // Orange - high workload
    return '#ff4d4f'; // Red - very high workload
  };

  const getWorkloadLabel = (workload: number): string => {
    if (workload < 50) return 'Low';
    if (workload < 75) return 'Medium';
    if (workload < 90) return 'High';
    return 'Very High';
  };

  const groupSlotsByDate = () => {
    const grouped = new Map<string, OpenSlot[]>();

    openSlots.forEach(slot => {
      const date = dayjs(slot.start_time).format('YYYY-MM-DD');
      if (!grouped.has(date)) {
        grouped.set(date, []);
      }
      grouped.get(date)!.push(slot);
    });

    // Sort slots within each date by start time
    grouped.forEach(slots => {
      slots.sort((a, b) => a.start_time.localeCompare(b.start_time));
    });

    return grouped;
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  const groupedSlots = groupSlotsByDate();
  const sortedDates = Array.from(groupedSlots.keys()).sort();

  return (
    <div className="open-slots-panel">
      {/* Header with Filters */}
      <Card className="filters-card" size="small">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <label>Date Range</label>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="MMM DD, YYYY"
                style={{ width: '100%' }}
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <label>Technician</label>
              <Select
                value={selectedTechId}
                onChange={setSelectedTechId}
                style={{ width: '100%' }}
                placeholder="All Technicians"
                allowClear
              >
                {technicianSummaries.map(tech => (
                  <Select.Option key={tech.id} value={tech.id}>
                    {tech.name} ({tech.available_slots} available)
                  </Select.Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <label>Min Duration</label>
              <Select
                value={durationFilter}
                onChange={setDurationFilter}
                style={{ width: '100%' }}
              >
                <Select.Option value={0}>Any Duration</Select.Option>
                <Select.Option value={30}>30+ minutes</Select.Option>
                <Select.Option value={60}>1+ hour</Select.Option>
                <Select.Option value={90}>1.5+ hours</Select.Option>
                <Select.Option value={120}>2+ hours</Select.Option>
                <Select.Option value={180}>3+ hours</Select.Option>
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchOpenSlots}
              style={{ marginTop: '22px', width: '100%' }}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Technician Summary Cards */}
      {!loading && technicianSummaries.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
          {technicianSummaries.map(tech => (
            <Col xs={24} sm={12} md={8} lg={6} key={tech.id}>
              <Card
                size="small"
                className={`tech-summary-card ${selectedTechId === tech.id ? 'selected' : ''}`}
                hoverable
                onClick={() => setSelectedTechId(selectedTechId === tech.id ? null : tech.id)}
              >
                <div className="tech-summary-header">
                  <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                  <div className="tech-summary-info">
                    <div className="tech-name">{tech.name}</div>
                    <div className="tech-stats">
                      {tech.available_slots} of {tech.total_slots} slots available
                    </div>
                  </div>
                </div>
                <div className="tech-summary-metrics">
                  <div className="metric">
                    <span className="metric-label">Workload</span>
                    <Tag color={getWorkloadColor(tech.avg_workload)}>
                      {tech.avg_workload.toFixed(0)}% - {getWorkloadLabel(tech.avg_workload)}
                    </Tag>
                  </div>
                  {tech.earliest_slot && (
                    <div className="metric">
                      <span className="metric-label">Next Available</span>
                      <span className="metric-value">
                        {dayjs(tech.earliest_slot).format('MMM DD, h:mm A')}
                      </span>
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Open Slots Timeline */}
      <Card
        title={
          <Space>
            <CalendarOutlined />
            Open Time Slots
            <Tag color="blue">{openSlots.filter(s => s.is_available).length} available</Tag>
          </Space>
        }
        style={{ marginTop: '16px' }}
      >
        {loading ? (
          <div className="loading-container">
            <Spin size="large" />
            <p>Loading available slots...</p>
          </div>
        ) : sortedDates.length === 0 ? (
          <Empty
            description="No available slots found in this date range"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <div className="slots-timeline">
            {sortedDates.map(date => {
              const dateSlots = groupedSlots.get(date)!;
              const availableSlots = dateSlots.filter(s => s.is_available);

              if (availableSlots.length === 0) return null;

              return (
                <div key={date} className="date-group">
                  <div className="date-header">
                    <CalendarOutlined />
                    <span className="date-label">{dayjs(date).format('dddd, MMMM D, YYYY')}</span>
                    <Tag color="blue">{availableSlots.length} slots</Tag>
                  </div>
                  <Row gutter={[12, 12]} className="slots-grid">
                    {availableSlots.map((slot, idx) => (
                      <Col xs={24} sm={12} md={8} lg={6} key={`${slot.technician_id}-${slot.start_time}-${idx}`}>
                        <Tooltip
                          title={
                            <div>
                              <div>{slot.technician_name}</div>
                              <div>Duration: {slot.duration_minutes} minutes</div>
                              <div>Workload: {slot.workload_percentage.toFixed(0)}%</div>
                            </div>
                          }
                        >
                          <Card
                            size="small"
                            className="slot-card"
                            hoverable
                            onClick={() => onSlotClick?.(slot)}
                            style={{
                              borderLeft: `4px solid ${getWorkloadColor(slot.workload_percentage)}`
                            }}
                          >
                            <div className="slot-tech">
                              <UserOutlined style={{ marginRight: '4px' }} />
                              {slot.technician_name}
                            </div>
                            <div className="slot-time">
                              <ClockCircleOutlined style={{ marginRight: '4px' }} />
                              {dayjs(slot.start_time).format('h:mm A')} - {dayjs(slot.end_time).format('h:mm A')}
                            </div>
                            <div className="slot-duration">
                              {slot.duration_minutes} min
                              <Tag
                                color={getWorkloadColor(slot.workload_percentage)}
                                style={{ marginLeft: '8px', fontSize: '11px' }}
                              >
                                {slot.workload_percentage.toFixed(0)}%
                              </Tag>
                            </div>
                          </Card>
                        </Tooltip>
                      </Col>
                    ))}
                  </Row>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};
